import { flatten } from 'flat';

import { CMSTrip, SanityUUID } from '../data/sanity/types';
import { SPORT_TAG_ID } from './constants';

export * from './stringHelpers';
export * from './typeHelpers';
export * from './urlHelpers';

export const isSSR = () => typeof window === 'undefined';

export const flattenPropertiesAsStrings = <
  T extends { [key: string | number]: unknown } | null | undefined,
>(
  obj: T
) => {
  if (!obj) {
    return {};
  }

  return propertiesAsStrings(
    flatten(obj) as { [key: string | number]: unknown }
  );
};

export const propertiesAsStrings = <
  T extends { [key: string | number]: unknown } | null | undefined,
>(
  source: T,
  excludeProperties: string[] = []
): { [key: string]: string } => {
  if (!source) {
    return {};
  }

  return Object.keys(source).reduce(
    (acc: { [key: string]: string }, key: string) => {
      const value = source[key];

      if (excludeProperties.includes(key)) {
        return acc;
      }

      if (typeof value === 'string') {
        acc[key] = value;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        acc[key] = value.toString();
      }

      return acc;
    },
    {}
  );
};

export const mapArrayToBooleanFlags = <T>(
  array: T[] | undefined | null,
  prefix: string,
  property: keyof T
): Record<string, string> => {
  if (!array) return {};

  return array.reduce(
    (acc, item) => ({
      ...acc,
      [`${prefix}_${item[property]}`]: 'true',
    }),
    {} as Record<string, string>
  );
};

export const extractUniqueTripIdsFromTripCollections = (
  collections: { tripIds: SanityUUID[] }[] | null | undefined,
  limit?: number
): SanityUUID[] =>
  Array.from(
    new Set(
      (collections ?? []).flatMap((collection) =>
        limit ? collection.tripIds.slice(0, limit) : collection.tripIds
      )
    )
  );

export const removeNestedPropertyFromObject = (
  obj: any,
  propertyToRemove: string
): any => {
  const removeFn = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeFn(item));
    } else if (typeof obj === 'object' && obj !== null) {
      const { [propertyToRemove]: _, ...rest } = obj;

      for (const key in rest) {
        rest[key] = removeFn(rest[key]);
      }

      return rest;
    }

    return obj;
  };

  return removeFn(obj);
};

export const formatPrice = (
  amount: number | undefined,
  locale: string | undefined,
  currency: string | undefined | null
): string | undefined => {
  return amount?.toLocaleString(locale, {
    style: 'currency',
    currency: currency ?? undefined,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

export const isTripTaggedWithSportTag = (trip: CMSTrip) =>
  trip.tags?.some((tag) => tag._id === SPORT_TAG_ID);
